import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, ArrowDownToLine, Loader2, X } from "lucide-react";
import Navbar from "./components/Navbar";

function App() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const pollInterval = useRef(null);

  const API_BASE_URL = `${window.location.origin}`;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowed = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowed.includes(selectedFile.type)) {
      alert("Please upload a JPG or PNG image.");
      return;
    }

    setProcessedImageUrl(null);
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setTaskId(null);
  };

  const startProcessing = async () => {
    if (!file) return;
    setIsProcessing(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await fetch(`${API_BASE_URL}/api/predict`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.task_id) {
        setTaskId(data.task_id);
      } else {
        throw new Error(data.error || "Failed to start processing.");
      }
    } catch (err) {
      alert("Error: " + err.message);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!taskId || !isProcessing) {
        if (pollInterval.current) {
            clearInterval(pollInterval.current);
            pollInterval.current = null;
        }
        return;
    }

    pollInterval.current = setInterval(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/status/${taskId}`);
            const data = await res.json();

            setProgress(prev => Math.max(prev, data.progress || 0));

            if (data.status === 'COMPLETED') {
                let imageSrc = data.processed_image;
                if (imageSrc && imageSrc.startsWith("/")) {
                  imageSrc = `${API_BASE_URL}${imageSrc}`;
                }
                setProcessedImageUrl(imageSrc);
                setIsProcessing(false);
                setTaskId(null);
                setProgress(100);
                clearInterval(pollInterval.current);
            } else if (data.status === 'FAILED') {
                alert("Processing failed: " + data.error);
                setIsProcessing(false);
                setTaskId(null);
                clearInterval(pollInterval.current);
            } else if (data.status === 'CANCELLED' || data.status === 'CANCELLING'){
                setIsProcessing(false);
                setTaskId(null);
                clearInterval(pollInterval.current);
            }
        } catch (err) {
            console.error("Polling error:", err);
        }
    }, 2000);

    return () => {
        if (pollInterval.current) {
            clearInterval(pollInterval.current);
        }
    };
}, [taskId, isProcessing]);


  const handleCancel = async () => {
    if (!taskId) return;
    try {
      await fetch(`${API_BASE_URL}/api/cancel/${taskId}`, { method: "POST" });
    } catch (err) {
      alert("Failed to cancel task.");
    }
  };

  const handleDownload = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "processed_image.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert("Download failed");
    }
  };

  return (
    <>
      <Navbar />
      <div className="flex flex-col items-center justify-center px-4 mt-12 text-gray-800 transition-colors sm:mt-12 lg:mt-20">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center w-full max-w-xl p-8 lg:max-w-lg sm:max-w-3xl sm:p-6 lg:p-4"
        >
          <label
            htmlFor="file-input"
            className="relative w-full overflow-hidden border-2 border-gray-300 border-dashed cursor-pointer h-[60vh] rounded-xl dark:border-gray-700 bg-gray-50 dark:bg-gray-800 group"
          >
            {processedImageUrl ? (
              <img src={processedImageUrl} alt="Processed result" className="object-contain w-full h-full" />
            ) : previewUrl ? (
              <img src={previewUrl} alt="Selected preview" className="object-contain w-full h-full" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                <Upload className="mb-2 text-gray-400 w-14 h-14 sm:w-24 sm:h-24 lg:w-12 lg:h-12 group-hover:text-blue-500" />
                <p className="text-base font-medium sm:text-3xl lg:text-base">Upload an image</p>
              </div>
            )}

            {(previewUrl || processedImageUrl) && !isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center text-base font-medium text-white transition opacity-0 sm:text-2xl lg:text-base group-hover:opacity-100 bg-black/30">
                Click to replace image
              </div>
            )}

            <input
              id="file-input"
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              onChange={handleFileChange}
              className="sr-only"
              disabled={isProcessing}
            />

            <AnimatePresence>
              {isProcessing && (
                <motion.div
                  className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-sm bg-white/60 dark:bg-black/60"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="w-20 h-20 border-4 border-blue-500 rounded-full sm:w-16 sm:h-16 border-t-transparent animate-spin"></div>
                  <p className="mt-4 text-lg font-medium text-center text-gray-800 sm:text-sm dark:text-gray-200">
                    Processing...
                    <br />
                    <span className="text-base text-gray-400 sm:text-2xl lg:text-base">It takes a few minutes</span>
                  </p>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 mt-6 text-base font-semibold text-red-500 bg-white rounded-full shadow-md sm:mt-4 lg:mt-6 lg:py-3 lg:px-6 sm:px-4 sm:py-2 sm:text-2xl lg:text-base hover:bg-gray-100 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </label>

          <div className="flex flex-col w-full gap-4 mt-8">
            <motion.button
              onClick={startProcessing}
              disabled={!file || isProcessing}
              whileTap={{ scale: 0.97 }}
              className={`${file && !isProcessing
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                } text-white font-semibold py-4 text-xl sm:py-4 lg:py-3 sm:text-3xl lg:text-lg rounded-xl flex items-center justify-center transition`}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 sm:w-24 sm:h-24 lg:w-6 lg:h-6 animate-spin" /> Processing...
                </>
              ) : (
                "Process Image"
              )}
            </motion.button>

            <motion.button
              onClick={() => processedImageUrl && handleDownload(processedImageUrl)}
              disabled={!processedImageUrl || isProcessing}
              whileTap={{ scale: 0.97 }}
              className={`${processedImageUrl && !isProcessing
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                } text-white font-semibold py-4 text-xl sm:py-4 lg:py-3 sm:text-3xl lg:text-lg rounded-xl flex items-center justify-center gap-3 transition`}
            >
              <ArrowDownToLine className="w-6 h-6 sm:w-10 sm:h-10 lg:w-6 lg:h-6" />
              Download
            </motion.button>
          </div>
        </motion.div>

        <p className="mt-0 text-sm text-center text-gray-400 sm:mt-0 lg:mt-4 sm:text-2xl lg:text-sm dark:text-gray-500">
          © {new Date().getFullYear()} Yahya Momtaz — MAGIC Project
        </p>
      </div>
    </>
  );
}

export default App;
